import { z } from "zod";
import { certificationTypeSchema, certificationStatusSchema } from "./certifications.js";
import { equipmentStatusSchema } from "./equipment.js";
import { geofenceStatusSchema, geolocationInputSchema, siteGeofenceConfigSchema } from "./geofence.js";
import { payBreakdownLineSchema } from "./pay-calculation.js";
import { paginationSchema } from "./common.js";

export const dailyEntryFlagsSchema = z.object({
  isWeekend: z.boolean().default(false),
  isHoliday: z.boolean().default(false),
  isNightShift: z.boolean().default(false),
  isHazard: z.boolean().default(false),
  isTraining: z.boolean().default(false),
  isManualOvertime: z.boolean().default(false),
  isManualDoubleTime: z.boolean().default(false),
});

export const dailyEntryEquipmentRowSchema = z.object({
  id: z.string().uuid().optional(),
  equipmentId: z.string().uuid(),
  usageHours: z.coerce.number().min(0),
  certificationOverrideReason: z.string().trim().max(500).optional().nullable(),
  equipmentHoursOverrideReason: z.string().trim().max(500).optional().nullable(),
});

export const dailyEntryRowInputSchema = z.object({
  id: z.string().uuid().optional(),
  workerId: z.string().uuid(),
  hours: z.coerce.number().min(0).max(24),
  jobCode: z.string().trim().max(64).optional().nullable(),
  costCode: z.string().trim().max(64).optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
  flags: dailyEntryFlagsSchema,
  equipment: z.array(dailyEntryEquipmentRowSchema).default([]),
});

export const dailyEntryQuerySchema = z.object({
  siteId: z.string().uuid(),
  date: z.string().date(),
});

export const dailyEntryWorkerSearchSchema = paginationSchema.extend({
  siteId: z.string().uuid(),
  q: z.string().trim().max(120).optional(),
  crew: z.string().trim().max(100).optional(),
  excludeWorkerIds: z
    .string()
    .optional()
    .transform((value) =>
      value
        ? value
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
        : [],
    ),
});

export const saveDailyEntrySchema = z.object({
  siteId: z.string().uuid(),
  date: z.string().date(),
  rows: z.array(dailyEntryRowInputSchema),
});

export const submitDailyEntrySchema = saveDailyEntrySchema.extend({
  geolocation: geolocationInputSchema.optional().nullable(),
  geofenceOverrideReason: z.string().trim().max(500).optional().nullable(),
  deviceInfo: z.string().trim().max(500).optional().nullable(),
});

export const dailyEntryPayPreviewSchema = z.object({
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  premiumHours: z.number(),
  grossPay: z.number().nullable(),
  equipmentCost: z.number().nullable(),
  payWarnings: z.array(z.string()).default([]),
  breakdown: z.array(payBreakdownLineSchema).default([]),
});

export const dailyEntryRowWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
  severity: z.enum(["warning", "error"]),
});

export const dailyEntryRowSchema = dailyEntryRowInputSchema.extend({
  id: z.string().uuid().optional(),
  employeeId: z.string(),
  workerName: z.string(),
  crew: z.string().nullable(),
  trade: z.string().nullable(),
  baseHourlyRate: z.number().nullable().optional(),
  ruleProfileId: z.string().uuid().nullable().optional(),
  status: z.enum(["DRAFT", "SUBMITTED", "APPROVED", "REJECTED", "LOCKED"]).optional(),
  payPreview: dailyEntryPayPreviewSchema,
  warnings: z.array(dailyEntryRowWarningSchema),
  certificationWarnings: z.array(z.string()),
  geofenceStatus: geofenceStatusSchema.nullable().optional(),
  canEdit: z.boolean(),
});

export const dailyEntrySummarySchema = z.object({
  workerCount: z.number().int(),
  totalHours: z.number(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  equipmentHours: z.number(),
  estimatedGrossPay: z.number().nullable(),
  equipmentCost: z.number().nullable(),
  exceptionCount: z.number().int(),
});

export const dailyEntryDaySchema = z.object({
  siteId: z.string().uuid(),
  date: z.string(),
  payPeriodId: z.string().uuid().nullable(),
  payPeriodStatus: z.string().nullable(),
  payPeriodLabel: z.string().nullable(),
  isLocked: z.boolean(),
  canSubmit: z.boolean(),
  canEdit: z.boolean(),
  rows: z.array(dailyEntryRowSchema),
  summary: dailyEntrySummarySchema,
  previousDate: z.string().nullable(),
  geofence: siteGeofenceConfigSchema,
});

export const dailyEntryWorkerOptionSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string(),
  displayName: z.string(),
  crew: z.string().nullable(),
  trade: z.string().nullable(),
  baseHourlyRate: z.number().nullable(),
  ruleProfileId: z.string().uuid().nullable(),
});

export const dailyEntryEquipmentOptionSchema = z.object({
  id: z.string().uuid(),
  equipmentId: z.string(),
  name: z.string(),
  type: z.enum(["HEAVY_EQUIPMENT", "VEHICLE", "TOOL", "RENTAL", "OTHER"]),
  hourlyCost: z.number().nullable(),
  requiredCertification: certificationTypeSchema.nullable(),
  status: equipmentStatusSchema,
  siteId: z.string().uuid().nullable(),
});

export const dailyEntryWorkerCertSummarySchema = z.object({
  type: certificationTypeSchema,
  status: certificationStatusSchema,
});

export const dailyEntryCrewMemberSchema = z.object({
  workerId: z.string().uuid(),
  employeeId: z.string(),
  displayName: z.string(),
  workerStatus: z.enum(["ACTIVE", "INACTIVE"]),
  defaultJobCode: z.string().nullable(),
  defaultCostCode: z.string().nullable(),
  defaultEquipmentId: z.string().uuid().nullable(),
});

export const dailyEntryCrewOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  workerIds: z.array(z.string().uuid()),
  members: z.array(dailyEntryCrewMemberSchema),
});

export const dailyEntryPayRuleSchema = z.object({
  id: z.string().uuid().optional(),
  type: z.string(),
  thresholdHours: z.number().nullable(),
  multiplier: z.number().nullable(),
  flatAmount: z.number().nullable(),
  percentagePremium: z.number().nullable(),
  priority: z.number().int(),
  active: z.boolean(),
});

export const dailyEntryPayRuleProfileBundleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  stackingMode: z.enum(["HIGHEST_MULTIPLIER_WINS", "STACK_MULTIPLIERS", "CUSTOM_PRIORITY"]),
  rules: z.array(dailyEntryPayRuleSchema),
});

export const dailyEntryOptionsSchema = z.object({
  siteId: z.string().uuid(),
  sites: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string() })),
  crews: z.array(dailyEntryCrewOptionSchema),
  /** Roster subset (crew members) — use worker search to add individuals. */
  workers: z.array(dailyEntryWorkerOptionSchema),
  workersById: z.record(z.string().uuid(), dailyEntryWorkerOptionSchema),
  equipment: z.array(dailyEntryEquipmentOptionSchema),
  workerCertificationsByWorkerId: z.record(
    z.string().uuid(),
    z.array(dailyEntryWorkerCertSummarySchema),
  ),
  payRuleProfile: z.object({
    id: z.string().uuid(),
    name: z.string(),
    stackingMode: z.enum(["HIGHEST_MULTIPLIER_WINS", "STACK_MULTIPLIERS", "CUSTOM_PRIORITY"]),
  }),
  payRules: z.array(dailyEntryPayRuleSchema),
  payRuleProfilesById: z.record(z.string().uuid(), dailyEntryPayRuleProfileBundleSchema),
  cumulativeWeeklyHours: z.record(z.string().uuid(), z.number()),
  recentJobCodes: z.array(z.string()),
});

export const submitDailyEntryResponseSchema = z.object({
  submitted: z.boolean(),
  siteId: z.string().uuid(),
  date: z.string(),
  summary: dailyEntrySummarySchema,
  geofenceStatus: geofenceStatusSchema.nullable().optional(),
  geofenceWarnings: z.array(z.string()).default([]),
  certificationWarnings: z.array(
    z.object({
      workerId: z.string().uuid(),
      equipmentId: z.string().uuid(),
      message: z.string(),
    }),
  ),
});

export type DailyEntryFlags = z.infer<typeof dailyEntryFlagsSchema>;
export type DailyEntryRowInput = z.infer<typeof dailyEntryRowInputSchema>;
export type DailyEntryRow = z.infer<typeof dailyEntryRowSchema>;
export type DailyEntryDay = z.infer<typeof dailyEntryDaySchema>;
export type DailyEntryOptions = z.infer<typeof dailyEntryOptionsSchema>;
export type DailyEntrySummary = z.infer<typeof dailyEntrySummarySchema>;
export type SaveDailyEntryInput = z.infer<typeof saveDailyEntrySchema>;
export type SubmitDailyEntryInput = z.infer<typeof submitDailyEntrySchema>;
export type SubmitDailyEntryResponse = z.infer<typeof submitDailyEntryResponseSchema>;
